import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Container,
  useTheme,
  Paper,
  Button,
  IconButton,
} from "@mui/material";
import { useUser } from "@clerk/clerk-react";
import { tokens } from "../../theme";
import LeagueTile from "../../components/LeagueTile";
import DraftboardPreview from "../../components/DraftboardPreview";
import SportsFootballIcon from "@mui/icons-material/SportsFootball";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSleeperConnection } from "../../hooks/useSleeperConnection";
import { useSleeperModal } from "../../contexts/SleeperModalContext";

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user } = useUser();
  const { isConnected, leagues: cachedLeagues, markAsConnected } = useSleeperConnection();
  const { openModal, isOpen: sleeperModalOpen } = useSleeperModal();

  const [leagues, setLeagues] = useState([]);
  const [leagueDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [showSleeperPrompt, setShowSleeperPrompt] = useState(false);
  const isFetchingRef = useRef(false);

  // Helper function to get the correct API base URL
  const getApiBaseUrl = () => {
    if (process.env.NODE_ENV === "production") {
      // Always use the hardcoded production URL to avoid environment variable issues
      return "https://weez-nfl-data.vercel.app";
    }
    // In development, return empty string to use the proxy
    return "";
  };

  // Helper function to get API headers with authentication
  const getApiHeaders = () => {
    const headers = {
      "Content-Type": "application/json",
    };

    // Add Clerk user headers
    if (user) {
      headers["X-Clerk-User-Id"] = user.id || "unknown";
      headers["X-Clerk-Email"] = user.primaryEmailAddress?.emailAddress || "";
      headers["X-Clerk-Username"] = user.username || "";
    }

    // Add the shared API key for backend authentication
    const apiKey = process.env.REACT_APP_API_SHARED_KEY;
    if (apiKey) {
      headers["X-API-KEY"] = apiKey;
    } else {
      console.warn("API Key is missing.");
    }

    return headers;
  };

  // Centralized function to fetch leagues and their details
  const fetchLeaguesAndDetails = async (isRefresh = false) => {
    if (isFetchingRef.current) {
      console.log('[Dashboard] Already fetching, skipping...');
      return;
    }
    isFetchingRef.current = true;

    console.log(`[Dashboard] Starting fetch - isRefresh: ${isRefresh}`);

    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const headers = getApiHeaders();
      const apiUrl = `${getApiBaseUrl()}/api/leagues/`;
      
      console.log(`[Dashboard] Fetching leagues from: ${apiUrl}`);
      const response = await fetch(apiUrl, { headers });

      if (response.status === 404) {
        console.log('[Dashboard] No leagues found (404)');
        setLeagues([]);
        // Don't clear cache on 404, let the hook handle cache expiry
        setShowSleeperPrompt(true);
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch leagues: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('[Dashboard] API Response data:', data);
      
      const leaguesArray = Array.isArray(data.results) ? data.results : 
                          Array.isArray(data) ? data : [];
      
      console.log('[Dashboard] Processed leagues array:', leaguesArray);
      setLeagues(leaguesArray);
      
      // Update the cache using markAsConnected if we have leagues
      if (leaguesArray.length > 0) {
        markAsConnected(leaguesArray);
        setShowSleeperPrompt(false); // Clear the prompt when leagues are found
      } else {
        // Show sleeper prompt if no leagues are found, regardless of connection status
        setShowSleeperPrompt(true);
      }
      
    } catch (err) {
      console.error("Error fetching leagues:", err);
      setError(err.message);
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
      isFetchingRef.current = false;
      console.log('[Dashboard] Fetch completed');
    }
  };

  // Initial data load effect
  useEffect(() => {
    // Use cached leagues on initial load if available
    if (cachedLeagues && cachedLeagues.length > 0) {
      console.log('[Dashboard] Using cached leagues:', cachedLeagues.length);
      setLeagues(cachedLeagues);
      setLoading(false);
      setShowSleeperPrompt(false); // Clear prompt when cached leagues are available
    } else if (user?.id && !isFetchingRef.current) {
      // Always attempt to fetch leagues when user is loaded, regardless of connection status
      // This ensures we check for existing leagues even if cache has expired
      console.log('[Dashboard] Fetching leagues for user');
      fetchLeaguesAndDetails();
    } else if (!user?.id) {
      // If no user, stop loading and don't show prompt yet
      console.log('[Dashboard] No user loaded');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, cachedLeagues?.length]);

  // Effect to refresh data when the Sleeper modal is closed
  useEffect(() => {
    if (!sleeperModalOpen && user?.id && leagues.length === 0 && !isFetchingRef.current) {
      console.log('[Dashboard] Modal closed, refreshing leagues');
      fetchLeaguesAndDetails(true); // Perform a refresh
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sleeperModalOpen, user?.id, leagues.length]);

  // Effect to handle when user becomes connected but we need to fetch leagues
  useEffect(() => {
    if (isConnected && leagues.length === 0 && !loading && !refreshing && !isFetchingRef.current) {
      console.log('[Dashboard] User connected but no leagues, fetching...');
      fetchLeaguesAndDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, leagues.length, loading, refreshing]);


  const handleConnectSleeper = () => {
    openModal();
  };

  const handleRefresh = () => {
    fetchLeaguesAndDetails(true); // Perform a refresh
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "50vh",
          gap: 2,
        }}
      >
        <CircularProgress size={60} sx={{ color: colors.blueAccent[500] }} />
        <Typography variant="h6" sx={{ color: colors.grey[300] }}>
          Loading your leagues...
        </Typography>
        <Typography variant="body2" sx={{ color: colors.grey[400] }}>
          This may take a few moments on first visit
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert
          severity="error"
          sx={{
            backgroundColor: colors.redAccent[800],
            color: colors.redAccent[100],
            "& .MuiAlert-icon": {
              color: colors.redAccent[300],
            },
            mb: 3,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Failed to load leagues
          </Typography>
          <Typography variant="body2">
            {error}. This might be a temporary issue. Please try refreshing or
            contact support if the problem persists.
          </Typography>
        </Alert>
        <Box sx={{ textAlign: "center" }}>
          <Button
            variant="contained"
            onClick={handleRefresh}
            startIcon={<SportsFootballIcon />}
            sx={{
              backgroundColor: colors.blueAccent[500],
              color: colors.primary[900],
              textTransform: "uppercase",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              "&:hover": {
                backgroundColor: colors.blueAccent[400],
              },
            }}
          >
            Try Again
          </Button>
        </Box>
      </Container>
    );
  }

  // Show Sleeper connection prompt only if no leagues are found (and not currently fetching)
  if (leagues.length === 0 && showSleeperPrompt && !loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        {/* Draftboard Preview Section - Show even without connection */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h3"
            sx={{
              color: colors.grey[100],
              fontWeight: 700,
              mb: 1,
            }}
          >
            Interactive Draftboard
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: colors.grey[300],
              mb: 3,
              fontSize: '1.1rem',
            }}
          >
            Experience our powerful grid-based draft visualization tool
          </Typography>
          <DraftboardPreview />
        </Box>

        {/* Connection Prompt */}
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: "center",
              backgroundColor: colors.primary[400],
              border: `1px solid ${colors.grey[700]}`,
              borderRadius: 2,
            }}
          >
          <LinkOutlinedIcon
            sx={{
              fontSize: 80,
              color: colors.blueAccent[500],
              mb: 2,
            }}
          />
          <Typography
            variant="h4"
            sx={{
              color: colors.grey[100],
              mb: 2,
              fontWeight: 600,
            }}
          >
            {leagues.length === 0
              ? "Connect to Sleeper"
              : "Reconnect to Sleeper"}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: colors.grey[300],
              mb: 3,
              maxWidth: 500,
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            {leagues.length === 0
              ? "Welcome to WeezDraftboard! To get started, you'll need to connect your Sleeper fantasy football account. This will import your leagues and allow you to track drafts, rosters, and more."
              : "It looks like your Sleeper connection has expired. Please reconnect to refresh your league data and continue using the app."}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: colors.grey[400],
              mb: 4,
              maxWidth: 400,
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            Click the "Connect to Sleeper" button in the top-right user menu to
            get started.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<LinkOutlinedIcon />}
            onClick={handleConnectSleeper}
            sx={{
              backgroundColor: colors.blueAccent[500],
              color: colors.primary[900],
              textTransform: "uppercase",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              "&:hover": {
                backgroundColor: colors.blueAccent[400],
              },
            }}
          >
            {leagues.length === 0
              ? "Connect to Sleeper"
              : "Reconnect to Sleeper"}
          </Button>
          </Paper>
        </Container>
      </Container>
    );
  }

  // Show import prompt if connected but no leagues
  if (leagues.length === 0 && isConnected) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            backgroundColor: colors.primary[400],
            border: `1px solid ${colors.grey[700]}`,
            borderRadius: 2,
          }}
        >
          <SportsFootballIcon
            sx={{
              fontSize: 80,
              color: colors.blueAccent[500],
              mb: 2,
            }}
          />
          <Typography
            variant="h4"
            sx={{
              color: colors.grey[100],
              mb: 2,
              fontWeight: 600,
            }}
          >
            Import Your Leagues
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: colors.grey[300],
              mb: 3,
              maxWidth: 500,
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            You're connected to Sleeper, but no leagues have been imported yet.
            This usually means you need to import leagues from a specific season
            or league ID.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: colors.grey[400],
              mb: 4,
              maxWidth: 400,
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            Click the "Connect to Sleeper" button in the top-right user menu to
            import your leagues.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<LinkOutlinedIcon />}
            onClick={handleConnectSleeper}
            sx={{
              backgroundColor: colors.blueAccent[500],
              color: colors.primary[900],
              textTransform: "uppercase",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              "&:hover": {
                backgroundColor: colors.blueAccent[400],
              },
            }}
          >
            Import Leagues
          </Button>
        </Paper>
      </Container>
    );
  }

  // Show success message when leagues are loaded
  if (leagues.length > 0 && !loading && !refreshing) {
    return (
      <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
        {/* Success Alert */}
        <Alert
          severity={isConnected ? "success" : "info"}
          sx={{
            mb: 3,
            backgroundColor: isConnected ? colors.greenAccent[800] : colors.blueAccent[800],
            color: isConnected ? colors.greenAccent[100] : colors.blueAccent[100],
            "& .MuiAlert-icon": {
              color: isConnected ? colors.greenAccent[300] : colors.blueAccent[300],
            },
          }}
        >
          <Typography variant="body1">
            {isConnected 
              ? `Successfully connected to Sleeper! You have ${leagues.length} league${leagues.length !== 1 ? "s" : ""} loaded.`
              : `You have ${leagues.length} cached league${leagues.length !== 1 ? "s" : ""}. ${!isConnected ? "Reconnect to Sleeper to refresh your data." : ""}`
            }
          </Typography>
        </Alert>

        {/* Draftboard Preview Section */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h3"
            sx={{
              color: colors.grey[100],
              fontWeight: 700,
              mb: 3,
            }}
          >
            Interactive Draftboard
          </Typography>
          <DraftboardPreview />
        </Box>

        {/* Header */}
        <Box
          sx={{
            mb: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              variant="h3"
              sx={{
                color: colors.grey[100],
                fontWeight: 700,
                mb: 1,
              }}
            >
              Your Fantasy Leagues
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: colors.grey[300],
                fontSize: "1.1rem",
              }}
            >
              {leagues.length} league{leagues.length !== 1 ? "s" : ""} found.
              Click on any league to view details and rosters.
              {refreshing && (
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <CircularProgress
                    size={16}
                    sx={{ color: colors.blueAccent[500] }}
                  />
                  Refreshing...
                </Box>
              )}
            </Typography>
            {/* Show warning if some league details failed to load */}
            {(() => {
              const failedLeagues = leagues.filter(
                (league) => !leagueDetails[league.external_league_id]
              );
              if (failedLeagues.length > 0) {
                return (
                  <Alert
                    severity="warning"
                    sx={{
                      mt: 1,
                      backgroundColor: colors.yellowAccent[800],
                      color: colors.yellowAccent[100],
                      "& .MuiAlert-icon": {
                        color: colors.yellowAccent[300],
                      },
                    }}
                  >
                    <Typography variant="body2">
                      Some league details may be incomplete. Use the refresh
                      button to try loading them again.
                    </Typography>
                  </Alert>
                );
              }
              return null;
            })()}
          </Box>
          <IconButton
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{
              color: colors.blueAccent[500],
              backgroundColor: colors.primary[500],
              border: `1px solid ${colors.grey[700]}`,
              "&:hover": {
                backgroundColor: colors.primary[400],
                borderColor: colors.blueAccent[400],
              },
              "&:disabled": {
                color: colors.grey[500],
                backgroundColor: colors.primary[600],
              },
            }}
            title="Refresh leagues"
          >
            {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Box>

        {/* Leagues Grid */}
        {refreshing ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress
              size={40}
              sx={{ color: colors.blueAccent[500], mb: 2 }}
            />
            <Typography variant="body1" sx={{ color: colors.grey[300] }}>
              Refreshing league data...
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {leagues.map((league) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={league.external_league_id}
              >
                <LeagueTile
                  league={league}
                  leagueDetails={leagueDetails[league.external_league_id]}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{
              color: colors.grey[100],
              fontWeight: 700,
              mb: 1,
            }}
          >
            Your Fantasy Leagues
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: colors.grey[300],
              fontSize: "1.1rem",
            }}
          >
            {leagues.length} league{leagues.length !== 1 ? "s" : ""} found.
            Click on any league to view details and rosters.
            {refreshing && (
              <Box
                component="span"
                sx={{
                  ml: 1,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <CircularProgress
                  size={16}
                  sx={{ color: colors.blueAccent[500] }}
                />
                Refreshing...
              </Box>
            )}
          </Typography>
          {/* Show warning if some league details failed to load */}
          {(() => {
            const failedLeagues = leagues.filter(
              (league) => !leagueDetails[league.external_league_id]
            );
            if (failedLeagues.length > 0) {
              return (
                <Alert
                  severity="warning"
                  sx={{
                    mt: 1,
                    backgroundColor: colors.yellowAccent[800],
                    color: colors.yellowAccent[100],
                    "& .MuiAlert-icon": {
                      color: colors.yellowAccent[300],
                    },
                  }}
                >
                  <Typography variant="body2">
                    Some league details may be incomplete. Use the refresh
                    button to try loading them again.
                  </Typography>
                </Alert>
              );
            }
            return null;
          })()}
        </Box>
        <IconButton
          onClick={handleRefresh}
          disabled={refreshing}
          sx={{
            color: colors.blueAccent[500],
            backgroundColor: colors.primary[500],
            border: `1px solid ${colors.grey[700]}`,
            "&:hover": {
              backgroundColor: colors.primary[400],
              borderColor: colors.blueAccent[400],
            },
            "&:disabled": {
              color: colors.grey[500],
              backgroundColor: colors.primary[600],
            },
          }}
          title="Refresh leagues"
        >
          {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
        </IconButton>
      </Box>

      {/* Leagues Grid */}
      {refreshing ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress
            size={40}
            sx={{ color: colors.blueAccent[500], mb: 2 }}
          />
          <Typography variant="body1" sx={{ color: colors.grey[300] }}>
            Refreshing league data...
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {leagues.map((league) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={league.external_league_id}
            >
              <LeagueTile
                league={league}
                leagueDetails={leagueDetails[league.external_league_id]}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default Dashboard;
